import { onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { getPageSEO, type SEOConfig } from '@/config/seo'

export function useSEO(customConfig?: Partial<SEOConfig>) {
  const route = useRoute()

  const updateSEO = (config: SEOConfig) => {
    // 更新页面标题
    document.title = config.title

    // 更新或创建meta标签
    const updateMetaTag = (name: string, content: string, property?: string) => {
      const selector = property ? `meta[property="${property}"]` : `meta[name="${name}"]`
      let metaTag = document.querySelector(selector) as HTMLMetaElement
      
      if (!metaTag) {
        metaTag = document.createElement('meta')
        if (property) {
          metaTag.setAttribute('property', property)
        } else {
          metaTag.setAttribute('name', name)
        }
        document.head.appendChild(metaTag)
      }
      
      metaTag.setAttribute('content', content)
    }

    // 基础SEO标签
    updateMetaTag('description', config.description)
    updateMetaTag('keywords', config.keywords.join(', '))
    
    // Open Graph标签
    updateMetaTag('og:title', config.ogTitle || config.title, 'og:title')
    updateMetaTag('og:description', config.ogDescription || config.description, 'og:description')
    updateMetaTag('og:type', 'website', 'og:type')
    updateMetaTag('og:url', window.location.href, 'og:url')
    updateMetaTag('og:site_name', '猫星球', 'og:site_name')
    updateMetaTag('og:locale', 'zh_CN', 'og:locale')
    
    // Twitter标签
    updateMetaTag('twitter:card', 'summary_large_image')
    updateMetaTag('twitter:title', config.ogTitle || config.title)
    updateMetaTag('twitter:description', config.ogDescription || config.description)
    
    // 其他SEO标签
    updateMetaTag('robots', 'index,follow')
    updateMetaTag('author', '猫星球')
    updateMetaTag('viewport', 'width=device-width, initial-scale=1.0')
    
    // 结构化数据
    if (config.structuredData) {
      updateStructuredData(config.structuredData)
    }
  }

  const updateStructuredData = (data: any) => {
    // 移除现有的结构化数据
    const existingScript = document.querySelector('script[type="application/ld+json"]')
    if (existingScript) {
      existingScript.remove()
    }
    
    // 添加新的结构化数据
    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.textContent = JSON.stringify(data)
    document.head.appendChild(script)
  }

  const initSEO = () => {
    const defaultConfig = getPageSEO(route.path)
    const finalConfig = { ...defaultConfig, ...customConfig }
    updateSEO(finalConfig)
  }

  // 监听路由变化
  watch(() => route.path, () => {
    initSEO()
  })

  onMounted(() => {
    initSEO()
  })

  return {
    updateSEO,
    initSEO
  }
}
